import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Chip, Drawer, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { getUtility } from '@livable/saas-react-components';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { track } from 'src/configs/amplitude';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { AUTO_HEIGHT_SCROLLBAR_ROOM } from 'src/utils/dataGrid';
import { AUTOMATION_COLORS, AUTOMATION_LABELS, automationPercent, coverageRowId, coverageSummary, filterCoverage } from './coverage';
import { FAILURE_CLASS_LABELS, STATUS_META, joinProviders, latestFullSweep, matchesProviderFilter, showsProblem, utilityKinds } from './model';
import { Card, KpiTiles, Loaded, SourceChip, Sparkline } from './parts';
import { useAsyncList } from './useAsyncList';
const FILTERS = [['supported', 'Supported'], ['unsupported', 'Not supported'], ['unused', 'Not in use'], ['needs_person', 'Needs a person'], ['walled', 'Walled'], ['failing', 'Login problems'], ['all', 'All']];
// Links written before the relabel still land on the same list.
const ALIASES = {
  manual: 'unsupported'
};
const MAX_MEMBERS = 25;

/** The app's own utility icons, with the raw type names on hover. */
// Small and capped: the static icon renders as a ~60px tile, so six of them crushed the provider name to one word a line.
const MAX_ICONS = 4;
const UtilityIcons = _ref => {
  let types = _ref.types;
  const kinds = utilityKinds(types || []);
  return kinds.length ? /*#__PURE__*/React.createElement(Tooltip, {
    title: (types || []).join(', ')
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 2,
    alignItems: "center",
    "aria-label": "Bills: ".concat(kinds.join(', ')),
    sx: {
      flexShrink: 0,
      '& svg': {
        width: 16,
        height: 16
      },
      '& > span > *': {
        padding: 0,
        minWidth: 0
      }
    }
  }, kinds.slice(0, MAX_ICONS).map(kind => /*#__PURE__*/React.createElement(Box, {
    key: kind,
    component: "span",
    sx: {
      display: 'inline-flex',
      width: 16,
      height: 16,
      overflow: 'hidden'
    }
  }, getUtility(kind).icon({
    svgType: 'static',
    fontSize: 'small'
  }))), kinds.length > MAX_ICONS ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, "+".concat(kinds.length - MAX_ICONS)) : null)) : null;
};
const familyCaption = row => row.catalogEntries === 0 ? "".concat(row.scraperProviderKey, " \xB7 no catalog entry") : [row.scraperProviderKey || 'no scraper', row.catalogEntries > 1 ? "".concat(row.catalogEntries, " catalog entries") : null, row.unmappedUtilities ? "".concat(row.unmappedUtilities.toLocaleString(), " utilities not mapped") : null].filter(Boolean).join(' · ');
const CHIP_TONE = {
  success: 'success',
  warning: 'warning',
  info: 'info',
  default: 'inactive'
};
const Section = _ref2 => {
  let title = _ref2.title,
    children = _ref2.children;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    gutterBottom: true
  }, title), children);
};
const ProviderDetail = _ref3 => {
  var _row$utilityTypes, _row$last$errors, _row$reg, _row$loginKeys;
  let row = _ref3.row,
    onClose = _ref3.onClose,
    onNavigate = _ref3.onNavigate;
  const _STATUS_META$row$stat = _slicedToArray(STATUS_META[row.statusKey], 2),
    label = _STATUS_META$row$stat[0],
    tone = _STATUS_META$row$stat[1];
  return /*#__PURE__*/React.createElement(Box, {
    role: "region",
    "aria-label": "".concat(row.name, " details")
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    spacing: 8
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, row.name), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, row.scraperProviderKey || 'no scraper key')), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    onClick: onClose
  }, "Close")), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 6,
    sx: {
      marginTop: '10px'
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: tone,
    label: label
  }), row.walled ? /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "inactive",
    label: "walled"
  }) : null, row.actionRequired && row.statusKey !== 'needs_person' ? /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "critical",
    label: "action required"
  }) : null), /*#__PURE__*/React.createElement(Section, {
    title: "Bills for"
  }, (_row$utilityTypes = row.utilityTypes) !== null && _row$utilityTypes !== void 0 && _row$utilityTypes.length ? /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 8,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(UtilityIcons, {
    types: row.utilityTypes
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, row.utilityTypes.join(', '))) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "Not recorded")), /*#__PURE__*/React.createElement(Section, {
    title: "Bills downloaded, last 14 full sweeps"
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      color: 'primary.main'
    }
  }, /*#__PURE__*/React.createElement(Sparkline, {
    values: row.series,
    width: 400,
    height: 80,
    label: "".concat(row.name, ": bills downloaded per full sweep")
  }))), /*#__PURE__*/React.createElement(Section, {
    title: "Schedule"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, row.streams ? "".concat(row.dueNow, " of ").concat(row.streams, " accounts due now").concat(row.nextWave ? " \xB7 next wave ".concat(row.nextWave) : '').concat(row.oldestLastPeriod ? " \xB7 oldest last bill ".concat(row.oldestLastPeriod) : '') : 'Nothing scheduled.')), row.logins ? /*#__PURE__*/React.createElement(Section, {
    title: "Logins"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "".concat(row.logins.active, " active \xB7 ").concat(row.logins.unverified, " not yet verified \xB7 ").concat(row.logins.needs_attention, " need attention \xB7 ").concat(row.logins.paused, " paused \xB7 ").concat(row.logins.disabled, " disabled"))) : null, row.members ? /*#__PURE__*/React.createElement(Section, {
    title: "Catalog entries (".concat(row.members.length, ")")
  }, row.members.length ? /*#__PURE__*/React.createElement(Stack, {
    spacing: 4
  }, row.members.slice(0, MAX_MEMBERS).map(member => /*#__PURE__*/React.createElement(Stack, {
    key: member.id,
    direction: "row",
    spacing: 8,
    alignItems: "center",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 6,
    alignItems: "center",
    sx: {
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    noWrap: true
  }, member.name), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "inactive",
    label: member.isCustom ? 'Custom' : 'Standard'
  }), member.mapped === false ? /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "warning",
    label: "not mapped"
  }) : null), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      fontVariantNumeric: 'tabular-nums'
    }
  }, member.utilities.toLocaleString()))), row.members.length > MAX_MEMBERS ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "and ".concat(row.members.length - MAX_MEMBERS, " more")) : null) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "No catalog entries. This scraper has no provider in Livable yet.")) : null, row.last && showsProblem(row.last) ? /*#__PURE__*/React.createElement(Section, {
    title: "Last problem"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, FAILURE_CLASS_LABELS[row.last.failure_class] || 'Unclassified', (_row$last$errors = row.last.errors) !== null && _row$last$errors !== void 0 && _row$last$errors.length ? " \xB7 ".concat(row.last.errors.join(', ')) : '')) : null, (_row$reg = row.reg) !== null && _row$reg !== void 0 && _row$reg.walled ? /*#__PURE__*/React.createElement(Section, {
    title: "Wall"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, row.reg.blocker || 'Blocked outside Livable.'), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, row.reg.retry_due ? 'Due for a quarterly retry.' : 'Not due for a retry yet.')) : null, (_row$loginKeys = row.loginKeys) !== null && _row$loginKeys !== void 0 && _row$loginKeys.length ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '20px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => onNavigate({
      view: 'logins',
      filter: "provider:".concat(row.loginKeys.join(','))
    })
  }, "Open logins for this provider")) : null, !row.last && row.automation === 'Automated' ? /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    sx: {
      marginTop: '16px'
    }
  }, "Not in the last full sweep.") : null);
};
const EmptyRows = _ref4 => {
  let searching = _ref4.searching,
    search = _ref4.search,
    filter = _ref4.filter;
  return /*#__PURE__*/React.createElement(Stack, {
    height: "100%",
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, searching ? "No provider matches \"".concat(search.trim(), "\".") : filter === 'supported' ? 'No supported providers yet.' : 'Nothing here.'));
};

/** Which providers are automated, how each did on the last full sweep, and how its logins are doing. */
export const ProvidersView = observer(_ref5 => {
  let health = _ref5.health,
    scoreboard = _ref5.scoreboard,
    credentials = _ref5.credentials,
    initialFilter = _ref5.filter,
    onNavigate = _ref5.onNavigate;
  const store = useStore();
  const coverage = useAsyncList(store.admin.getBillScrapingProvidersAsync, () => store.admin.getBillScrapingProvidersAsync.run(), 'Could not load provider coverage.', [store]);
  const start = ALIASES[initialFilter] || initialFilter;
  const _useState = useState(FILTERS.some(_ref6 => {
      let _ref7 = _slicedToArray(_ref6, 1),
        k = _ref7[0];
      return k === start;
    }) ? start : 'supported'),
    _useState2 = _slicedToArray(_useState, 2),
    filter = _useState2[0],
    setFilter = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    search = _useState4[0],
    setSearch = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    openKey = _useState6[0],
    setOpenKey = _useState6[1];
  const returnFocus = useRef(null);
  const sweep = latestFullSweep(health.rows);
  const joined = useMemo(() => joinProviders(coverage.rows, health.rows, scoreboard.rows[0], credentials.rows), [coverage.rows, health.rows, scoreboard.rows, credentials.rows]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(_ref8 => {
    let _ref9 = _slicedToArray(_ref8, 1),
      k = _ref9[0];
    return [k, joined.filter(r => matchesProviderFilter(r, k)).length];
  })), [joined]);
  // "Is X supported?" is asked by name, so a search looks at every provider whatever chip is on.
  const searching = !!search.trim();
  const rows = useMemo(() => filterCoverage(joined, {
    search
  }).filter(r => searching || matchesProviderFilter(r, filter)), [joined, search, searching, filter]);
  const summary = useMemo(() => coverageSummary(coverage.rows), [coverage.rows]);
  const open = rows.find(r => coverageRowId(r) === openKey);
  const handleFilter = key => {
    setSearch('');
    setFilter(key);
    track('Bill Automation Providers Filtered', {
      filter: key
    });
  };
  const handleOpen = (row, event) => {
    returnFocus.current = (event === null || event === void 0 ? void 0 : event.currentTarget) || null;
    setOpenKey(coverageRowId(row));
    track('Bill Automation Provider Opened', {
      automation: row.automation,
      status: row.statusKey
    });
  };
  const handleClose = () => {
    var _returnFocus$current, _returnFocus$current$;
    setOpenKey(null);
    (_returnFocus$current = returnFocus.current) === null || _returnFocus$current === void 0 || (_returnFocus$current$ = _returnFocus$current.focus) === null || _returnFocus$current$ === void 0 || _returnFocus$current$.call(_returnFocus$current);
  };
  const columns = useMemo(() => [{
    field: 'name',
    headerName: 'Provider',
    flex: 2,
    minWidth: 360,
    isExportable: true,
    renderCell: _ref0 => {
      let row = _ref0.row;
      return /*#__PURE__*/React.createElement(Stack, {
        spacing: 2,
        sx: {
          paddingY: '8px',
          minWidth: 0,
          width: '100%'
        }
      }, /*#__PURE__*/React.createElement(Tooltip, {
        title: row.name
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        noWrap: true,
        sx: {
          fontWeight: 600
        }
      }, row.name)), /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 6,
        alignItems: "center",
        sx: {
          minWidth: 0
        }
      }, /*#__PURE__*/React.createElement(UtilityIcons, {
        types: row.utilityTypes
      }), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "text.secondary",
        noWrap: true
      }, familyCaption(row))));
    }
  }, {
    // Export only: the exporter reads valueGetterForExport only on columns with a renderCell.
    field: 'kinds',
    headerName: 'Bills for',
    isExportable: true,
    renderCell: _ref1 => {
      let row = _ref1.row;
      return utilityKinds(row.utilityTypes || []).join(', ');
    },
    valueGetterForExport: _ref10 => {
      let row = _ref10.row;
      return utilityKinds(row.utilityTypes || []).join(', ');
    }
  }, {
    field: 'scraperProviderKey',
    headerName: 'Scraper key',
    isExportable: true
  }, {
    field: 'automation',
    headerName: 'Supported?',
    width: 200,
    isExportable: true,
    renderCell: _ref11 => {
      let row = _ref11.row;
      return /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 4,
        flexWrap: "wrap",
        useFlexGap: true,
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        variant: CHIP_TONE[AUTOMATION_COLORS[row.automation]] || 'inactive',
        label: AUTOMATION_LABELS[row.automation] || row.automation
      }), row.walled ? /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        variant: "inactive",
        label: "walled"
      }) : null, row.actionRequired && row.statusKey !== 'needs_person' ? /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        variant: "critical",
        label: "action required"
      }) : null);
    }
  }, {
    field: 'statusKey',
    headerName: 'Last full sweep',
    width: 170,
    isExportable: true,
    renderCell: _ref12 => {
      let row = _ref12.row;
      return /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        variant: STATUS_META[row.statusKey][1],
        label: STATUS_META[row.statusKey][0]
      });
    },
    valueGetterForExport: _ref13 => {
      let row = _ref13.row;
      return STATUS_META[row.statusKey][0];
    }
  }, {
    field: 'loginsActive',
    headerName: 'Logins',
    width: 170,
    isExportable: true,
    valueGetter: _ref14 => {
      var _row$logins$active, _row$logins;
      let row = _ref14.row;
      return (_row$logins$active = (_row$logins = row.logins) === null || _row$logins === void 0 ? void 0 : _row$logins.active) !== null && _row$logins$active !== void 0 ? _row$logins$active : 0;
    },
    renderCell: _ref15 => {
      let row = _ref15.row;
      return row.logins ? /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, "".concat(row.logins.active, " / ").concat(row.logins.logins), row.logins.needs_attention ? /*#__PURE__*/React.createElement(Typography, {
        component: "span",
        variant: "caption",
        color: "error",
        sx: {
          marginLeft: '6px'
        }
      }, "".concat(row.logins.needs_attention, " need attention")) : null) : '-';
    },
    valueGetterForExport: _ref16 => {
      let row = _ref16.row;
      return row.logins ? "".concat(row.logins.active, "/").concat(row.logins.logins) : '-';
    }
  }, {
    field: 'utilities',
    headerName: 'Utilities',
    width: 100,
    type: 'number',
    isExportable: true
  }, {
    field: 'postedMtd',
    headerName: 'Posted MTD',
    width: 120,
    type: 'number',
    isExportable: true,
    valueGetter: _ref17 => {
      var _row$bills$posted_mtd, _row$bills;
      let row = _ref17.row;
      return (_row$bills$posted_mtd = (_row$bills = row.bills) === null || _row$bills === void 0 ? void 0 : _row$bills.posted_mtd) !== null && _row$bills$posted_mtd !== void 0 ? _row$bills$posted_mtd : 0;
    }
  }, {
    field: 'verifiedMtd',
    headerName: 'Verified MTD',
    width: 120,
    type: 'number',
    isExportable: true,
    valueGetter: _ref18 => {
      var _row$bills$verified_m, _row$bills2;
      let row = _ref18.row;
      return (_row$bills$verified_m = (_row$bills2 = row.bills) === null || _row$bills2 === void 0 ? void 0 : _row$bills2.verified_mtd) !== null && _row$bills$verified_m !== void 0 ? _row$bills$verified_m : 0;
    }
  }, {
    field: 'dueNow',
    headerName: 'Due now',
    width: 110,
    isExportable: true,
    renderCell: _ref19 => {
      let row = _ref19.row;
      return row.streams ? "".concat(row.dueNow, " of ").concat(row.streams) : '-';
    },
    valueGetterForExport: _ref20 => {
      let row = _ref20.row;
      return row.streams ? "".concat(row.dueNow, "/").concat(row.streams) : '-';
    }
  }, {
    field: 'series',
    headerName: 'Trend',
    width: 130,
    sortable: false,
    renderCell: _ref21 => {
      let row = _ref21.row;
      return /*#__PURE__*/React.createElement(Box, {
        sx: {
          color: 'primary.main'
        }
      }, /*#__PURE__*/React.createElement(Sparkline, {
        values: row.series,
        label: "".concat(row.name, ": bills downloaded per full sweep")
      }));
    }
  }], []);
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(KpiTiles, {
    tiles: [['Supported providers', summary.supported], ['Supported and in use', summary.automated],
    // Same rule as the Not supported chip, so the two never disagree.
    ['Not supported, in use', counts.unsupported], ['On a supported provider', "".concat(summary.automatedUtilities.toLocaleString(), " of ").concat((summary.automatedUtilities + summary.manualUtilities).toLocaleString(), " (").concat(automationPercent(coverage.rows), "%)")]].map(_ref22 => {
      let _ref23 = _slicedToArray(_ref22, 2),
        label = _ref23[0],
        value = _ref23[1];
      return {
        label,
        value: coverage.loading || coverage.error ? '-' : value
      };
    })
  }), /*#__PURE__*/React.createElement(Card, {
    source: sweep ? /*#__PURE__*/React.createElement(SourceChip, {
      prod: true,
      at: sweep.finished_at
    }) : null
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 8,
    flexWrap: "wrap",
    useFlexGap: true,
    alignItems: "center",
    sx: {
      marginBottom: '12px'
    }
  }, FILTERS.map(_ref24 => {
    let _ref25 = _slicedToArray(_ref24, 2),
      key = _ref25[0],
      label = _ref25[1];
    return /*#__PURE__*/React.createElement(Chip, {
      key: key,
      label: coverage.loading || coverage.error ? label : "".concat(label, " \xB7 ").concat(counts[key].toLocaleString()),
      variant: !searching && filter === key ? 'info' : 'inactive',
      onClick: () => handleFilter(key),
      "aria-pressed": !searching && filter === key
    });
  }), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Search provider",
    value: search,
    onChange: e => setSearch(e.target.value),
    sx: {
      minWidth: 200
    }
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => exportCSVFromDataGrid({
      columns,
      rows,
      name: 'provider-coverage.csv'
    })
  }, "Export CSV")), searching ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      margin: '-4px 0 10px'
    }
  }, "Searching every provider.") : null, [[health, 'Sweep health did not load, so every status reads Never ran.'], [credentials, 'Logins did not load, so login counts are missing.'], [scoreboard, 'The scoreboard did not load, so month-to-date bills are missing.']].filter(_ref26 => {
    let _ref27 = _slicedToArray(_ref26, 1),
      state = _ref27[0];
    return state.error;
  }).map(_ref28 => {
    let _ref29 = _slicedToArray(_ref28, 2),
      message = _ref29[1];
    return /*#__PURE__*/React.createElement(Alert, {
      key: message,
      severity: "warning",
      sx: {
        marginBottom: '8px'
      }
    }, message);
  }), /*#__PURE__*/React.createElement(Loaded, {
    state: coverage
  }, /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    sx: _objectSpread(_objectSpread({}, AUTO_HEIGHT_SCROLLBAR_ROOM), {}, {
      '& .MuiDataGrid-row': {
        cursor: 'pointer'
      }
    }),
    columns: columns,
    rows: rows,
    getRowId: coverageRowId,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    onRowClick: (params, event) => handleOpen(params.row, event),
    initialState: {
      columns: {
        columnVisibilityModel: {
          kinds: false,
          scraperProviderKey: false
        }
      },
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      },
      sorting: {
        sortModel: [{
          field: 'utilities',
          sort: 'desc'
        }]
      }
    },
    pageSizeOptions: [25, 50, 100],
    slots: {
      noRowsOverlay: () => /*#__PURE__*/React.createElement(EmptyRows, {
        searching: searching,
        search: search,
        filter: filter
      })
    }
  }))), /*#__PURE__*/React.createElement(Drawer, {
    anchor: "right",
    open: !!open,
    onClose: handleClose,
    PaperProps: {
      sx: {
        width: {
          xs: '100%',
          sm: 440
        },
        padding: '20px'
      }
    }
  }, open ? /*#__PURE__*/React.createElement(ProviderDetail, {
    row: open,
    onClose: handleClose,
    onNavigate: onNavigate
  }) : null));
});